import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock, ExternalLink, ArrowLeft } from 'lucide-react';
import { erfuelltRecht } from '@symphenia/shared-types';
import type { ModuleKey } from '@symphenia/shared-types';
import { tenantsApi } from '@/features/tenants/api/tenants.api';
import { useAuthStore } from '@/lib/auth.store';
import { useTerminology } from '@/lib/useTerminology';
import { pageTitle, formCard } from '@/lib/form-styles';

/**
 * AP-29-D — „Kein Zugang" statt stiller Weiterleitung.
 *
 * Bis hierher lief eine gesperrte Route auf `<Navigate to="/" />`: Der Klick
 * landete wortlos am Dashboard, und für den Benutzer sah es aus, als sei die
 * Anwendung kaputt.
 *
 * Die Seite unterscheidet die **zwei Lagen**, die im Backend `MODULE_NOT_ACTIVE`
 * und `MODULE_SEAT_MISSING` heißen — sie führen zu verschiedenen
 * Ansprechpartnern, und genau daran scheitert sonst jede Supportanfrage:
 *
 * - Der Betrieb hat das Modul **nicht gebucht** → das entscheidet der Vertrieb,
 *   und anfragen kann es nur ein Administrator.
 * - Der Betrieb hat es, **dieser Benutzer** aber keinen Platz → das erledigt der
 *   eigene Administrator in zwei Klicks.
 */
export default function ModuleLockedPage({ moduleKey }: { moduleKey: ModuleKey }) {
  const { t } = useTranslation();
  const terms = useTerminology();
  const user = useAuthStore((s) => s.user);
  const [gebucht, setGebucht] = useState<boolean | null>(null);

  // AP-30-D: Wer die Buchung ändern kann, ist der, der die Module sieht —
  // dasselbe Recht wie die Seite, auf die der Hinweis verweist.
  const istAdmin = erfuelltRecht('core.module.read', user?.permissions);

  useEffect(() => {
    // Den Grund kennt nur der Server. Nur Administratoren dürfen den Endpunkt —
    // für alle anderen bleibt es beim allgemeinen Hinweis, und das ist richtig
    // so: Ein Mitarbeiter soll nicht erfahren, was sein Betrieb gebucht hat.
    if (!istAdmin) return;
    let abgebrochen = false;
    tenantsApi
      .getModules()
      .then((res) => {
        if (abgebrochen) return;
        const eintrag = res.data.catalog?.find((m) => m.key === moduleKey);
        setGebucht(eintrag?.bookedForTenant ?? false);
      })
      .catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, [istAdmin, moduleKey]);

  const name = moduleKey === 'PROJECT_MGMT' ? terms.management : t(`modules.names.${moduleKey}`);

  // Gebucht, aber hier gelandet → es fehlt der Platz. Für Nicht-Administratoren
  // ist das der wahrscheinlichere Fall und die hilfreichere Auskunft.
  const fehltNurDerPlatz = gebucht === true || (!istAdmin && gebucht === null);

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className={formCard}>
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-muted p-3">
            <Lock size={22} className="text-muted-foreground" />
          </div>
          <div className="flex-1">
            <h1 className={pageTitle}>{t('modules.locked.title', { module: name })}</h1>

            <p className="mt-3 text-sm text-muted-foreground">
              {fehltNurDerPlatz
                ? t('modules.locked.noSeat', { module: name })
                : t('modules.locked.notBooked', { module: name })}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                <ArrowLeft size={14} />
                {t('modules.locked.back')}
              </Link>

              {istAdmin && !fehltNurDerPlatz && (
                <a
                  href="https://www.symphenia.com/kontakt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  {t('modules.contactSales')}
                  <ExternalLink size={13} />
                </a>
              )}

              {istAdmin && (
                <Link
                  to="/settings/modules"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {t('modules.locked.overview')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
